<template>
    <h4 class="section-title">{{ translate("federation.billing_details") }}</h4>

    <BillingDetailsForm v-model="store.billingDetails" :validation-errors="store.billingValidationErrors" />

    <hr />

    <div class="buttons">
        <LoadingButton :is-loading="savingInProgress" class="button is-primary" @click="save">
            <span class="icon">
                <i class="fas fa-check"></i>
            </span>
            <span>{{ translate("common.save") }}</span>
        </LoadingButton>

        <button class="button" @click="reset">
            <span class="icon">
                <i class="fas fa-times"></i>
            </span>
            <span>{{ translate("common.reset") }}</span>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { useFederationDashboardStore } from "./federationManagementStore";
    import { onMounted, ref } from "vue";
    import federationsApi from "../../Api/federationsApi";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { translate } from "../../Common/translations";
    import HttpError from "../../Api/Utils/HttpError";
    import { ValidationError } from "../../Common/types";
    import BillingDetailsForm from "../../Common/BillingDetailsForm.vue";

    const store = useFederationDashboardStore();
    const savingInProgress = ref(false);

    function reset() {
        store.clearBillingValidationErrors();
        return store.refreshBillingDetails().catch(handleError);
    }

    function updateValidationErrors(validationErrors: ValidationError[]) {
        store.clearBillingValidationErrors();
        const errors: Record<string, string[]> = {};
        validationErrors.forEach((error: ValidationError) => {
            errors[error.field] = error.message;
        });
        store.setBillingValidationErrors(errors);
    }

    function save() {
        savingInProgress.value = true;
        store.clearBillingValidationErrors();

        return federationsApi
            .updateBillingDetails({
                federationId: store.federationId,
                billingDetails: store.billingDetails,
            })
            .then(() => {
                store.clearBillingValidationErrors();
                successToast(translate("federation.billing.updated"));
                return store.refreshBillingDetails();
            })
            .catch((err: HttpError & { validationErrors?: ValidationError[] }) => {
                if (err.validationErrors) {
                    updateValidationErrors(err.validationErrors);
                    return;
                }
                handleError(err);
            })
            .finally(() => {
                savingInProgress.value = false;
            });
    }

    onMounted(() => {
        store.refreshBillingDetails().catch(handleError);
    });
</script>

<style scoped></style>
